/**
 * Client-only feature flags for the server-driven forms preview. Backed by the app's Redux store
 * (`src/Store`) — in-memory only, intentionally NOT persisted, so a page reload resets both flags to
 * their defaults (OFF). A synchronous get/set API is kept so non-React call sites (e.g. render paths in
 * the create panels) can read a flag without a hook.
 *
 * Toggled from the NavBar Settings panel. `ServerDrivenForm` reads `legacyRecompute` once per mount, so
 * the next form open picks up a change. Note: Microsoft Marketplace is no longer gated by
 * `serverDrivenForms` — its visibility is controlled by the `MarketplaceTenant` flight (see
 * `App.tsx`'s tenant injection + `AuthorizationInfo.isIncludedInRequiredFlights`).
 */
import { store } from 'src/Store';
import { setServerDrivenForms, setLegacyRecompute } from 'src/reducers/featureFlagsReducer';

/** True when the user has opted into the server-driven forms preview. Defaults to OFF. */
export const getServerDrivenFormsFlag = (): boolean =>
  store.getState().featureFlags?.serverDrivenForms === true;

/** Set the server-driven forms preview preference (in-memory Redux state). */
export const setServerDrivenFormsFlag = (on: boolean): void => {
  store.dispatch(setServerDrivenForms(on));
};

/**
 * Escape hatch for the reactive-field recompute gating in the server-driven forms. Defaults to OFF
 * (gating active: `/view` fires only for fields the engine marks `reactive`, plus one settle-up
 * compute at submit). Turn ON to revert to the legacy behavior of recomputing on every field edit —
 * e.g. if a stale-UI bug is suspected in the engine's reactive hints.
 */
export const getLegacyRecomputeFlag = (): boolean =>
  store.getState().featureFlags?.legacyRecompute === true;

/** Set the legacy-recompute escape hatch (in-memory Redux state). */
export const setLegacyRecomputeFlag = (on: boolean): void => {
  store.dispatch(setLegacyRecompute(on));
};
// Generated by GitHub Copilot | Updated on: 2026-07-22 | Removed getServerDrivenManageFormsFlag/setServerDrivenManageFormsFlag facade: Marketplace manage form is no longer flag-gated, visibility is controlled by the MarketplaceTenant flight instead
// Generated by GitHub Copilot | Created on: 2026-06-24 | New file | localStorage feature-flag helpers for the server-driven forms preview toggle
// Generated by Claude Code | Updated on: 2026-07-21 | Added legacyRecompute escape hatch: reverts the server-driven forms to recompute-on-every-edit when the reactive gating misbehaves
// Generated by GitHub Copilot | Updated on: 2026-07-21 | 40 lines modified | Moved flags off localStorage to in-memory Redux store (facade over src/Store); flags no longer persist across reloads
